/**
 * Noticing that the page is running yesterday's code.
 *
 * A single-page app keeps its bundle in memory for as long as the tab is open. Deploy a fix and a
 * tab that was opened this morning carries on running the old one — silently, with no visible sign.
 * That cost most of a working day: every fix was deployed and verified on the server, and none of
 * them reached the person reporting the fault, so each "still failed" looked like a new bug.
 *
 * The page knows which bundle it loaded. The server serves the current one. Comparing the two is
 * enough — no build stamps, no service worker, nothing to keep in step.
 */

/** The hashed asset this page actually loaded, e.g. `index-DaL2TKVd.js`. */
export function loadedBundle(): string | null {
  const el = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
  return el?.src.split('/').pop() ?? null;
}

/** The hashed asset the server is serving right now. Null when it cannot be determined. */
export async function servedBundle(signal?: AbortSignal): Promise<string | null> {
  try {
    // `cache: 'no-store'` matters: without it the browser may answer from the very cache that
    // caused the problem.
    const res = await fetch('/index.html', { cache: 'no-store', ...(signal ? { signal } : {}) });
    if (!res.ok) return null;
    const html = await res.text();
    return /\/assets\/(index-[A-Za-z0-9_-]+\.js)/.exec(html)?.[1] ?? null;
  } catch {
    // Offline, or the deploy is mid-restart. Not knowing is not the same as being out of date.
    return null;
  }
}

/** True when the server has a different build from the one this page is running. */
export async function isStale(signal?: AbortSignal): Promise<boolean> {
  const mine = loadedBundle();
  if (mine === null) return false;
  const theirs = await servedBundle(signal);
  return theirs !== null && theirs !== mine;
}
